import { useState } from "react";
import { Check, Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LeadTagBadge } from "./LeadTagBadge";
import { useLeadTags } from "@/hooks/useLeadTags";
import { TAG_COLORS } from "@/types/lead";

interface LeadTagSelectorProps {
  workspaceId: string;
  leadId: string;
}

export function LeadTagSelector({ workspaceId, leadId }: LeadTagSelectorProps) {
  const [searchValue, setSearchValue] = useState("");
  const [selectedColor, setSelectedColor] = useState(TAG_COLORS[0].value);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const {
    workspaceTags,
    leadTags,
    loading,
    createTag,
    assignTagToLead,
    removeTagFromLead,
  } = useLeadTags({ workspaceId, leadId });

  const filteredTags = workspaceTags.filter((tag) =>
    tag.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const exactMatch = workspaceTags.find(
    (t) => t.name.toLowerCase() === searchValue.toLowerCase()
  );

  const handleCreateTag = async () => {
    if (!searchValue.trim()) return;

    const newTag = await createTag(searchValue.trim(), selectedColor);
    if (newTag) {
      await assignTagToLead(newTag.id);
      setSearchValue("");
      setShowCreateForm(false);
    }
  };

  const handleToggleTag = async (tagId: string) => {
    const isSelected = leadTags.some((t) => t.id === tagId);
    if (isSelected) {
      await removeTagFromLead(tagId);
    } else {
      await assignTagToLead(tagId);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-9 bg-muted rounded-md" />
        <div className="flex gap-2">
          <div className="h-6 w-16 bg-muted rounded-full" />
          <div className="h-6 w-20 bg-muted rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Tags selecionadas */}
      {leadTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {leadTags.map((tag) => (
            <LeadTagBadge
              key={tag.id}
              tag={tag}
              onRemove={() => removeTagFromLead(tag.id)}
            />
          ))}
        </div>
      )}

      {/* Campo de busca/criação */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar ou criar tag..."
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setShowCreateForm(e.target.value.length > 0 && !exactMatch);
          }}
          className="pl-8 h-9"
        />
      </div>

      {/* Lista de tags existentes */}
      {(searchValue || workspaceTags.length > 0) && (
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {filteredTags.map((tag) => {
            const isSelected = leadTags.some((t) => t.id === tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleToggleTag(tag.id)}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors",
                  isSelected
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-muted"
                )}
              >
                <div
                  className={cn("w-3 h-3 rounded-full", tag.color)}
                />
                <span className="flex-1 text-left">{tag.name}</span>
                {isSelected && <Check className="w-4 h-4" />}
              </button>
            );
          })}

          {filteredTags.length === 0 && searchValue && (
            <p className="text-sm text-muted-foreground px-2 py-1">
              Nenhuma tag encontrada
            </p>
          )}
        </div>
      )}

      {/* Formulário de criação */}
      {showCreateForm && searchValue && !exactMatch && (
        <div className="border rounded-md p-3 space-y-3 bg-muted/30">
          <p className="text-sm font-medium">
            Criar tag "{searchValue}"
          </p>
          
          <div className="flex flex-wrap gap-1.5">
            {TAG_COLORS.map((color) => (
              <button
                key={color.value}
                type="button"
                onClick={() => setSelectedColor(color.value)}
                className={cn(
                  "w-6 h-6 rounded-full transition-all",
                  color.value,
                  selectedColor === color.value
                    ? "ring-2 ring-offset-2 ring-primary"
                    : "hover:scale-110"
                )}
                title={color.name}
              />
            ))}
          </div>

          <Button
            type="button"
            size="sm"
            onClick={handleCreateTag}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-1" />
            Criar e Adicionar
          </Button>
        </div>
      )}
    </div>
  );
}
